import { DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSelectModule } from '@angular/material/select';
import { LocaleService } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';

import {
  BREEDER_REGISTRATION_COLUMNS,
  BREEDER_REGISTRATION_PARAMETERS,
  DEFAULT_BREEDER_WEEK_REGISTRATION_FILTERS,
} from '../../../../../assets/data/weekRegistrationFilters';
import { AppState } from '../../../../shared/models';
import { SharedModule } from '../../../../shared/shared.module';
import { Flock, FlockWeekData, FlockWeekDataSet } from '../../../models/flock.model';
import { loadBreederFlockChart } from '../../../store/actions/flock.actions';
import { getWeekRegistrationFilters } from '../../../store/selectors/flock.selectors';

interface BreederColumn {
  parameter: string;
  decimals: number;
  unit: string;
  chartable: boolean;
}

@Component({
  selector: 'app-breeder-flock-overview-week-registration-detailed',
  imports: [DatePipe, MatSelectModule, SharedModule],
  templateUrl: './flock-overview-week-registration-detailed.html',
  styleUrl: './flock-overview-week-registration-detailed.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreederFlockOverviewWeekRegistrationDetailed {
  readonly flock = input.required<Flock>();
  readonly flockWeekDataSet = input.required<FlockWeekDataSet>();

  private readonly decimalPipe = inject(DecimalPipe);
  private readonly locale = inject(LocaleService).getLocale();
  private readonly store = inject<Store<AppState>>(Store);
  private readonly filters = toSignal(
    this.store
      .select(getWeekRegistrationFilters)
      .pipe(map((filters) => filters ?? DEFAULT_BREEDER_WEEK_REGISTRATION_FILTERS)),
    { initialValue: DEFAULT_BREEDER_WEEK_REGISTRATION_FILTERS },
  );

  protected readonly columns = computed<BreederColumn[]>(() => {
    const dataSeries = this.flockWeekDataSet().dataSeries;
    const graphSeries = dataSeries?.series?.[this.flock().id] ?? [];
    const filters = this.filters();

    return [
      ...BREEDER_REGISTRATION_COLUMNS.filter(({ parameter }) =>
        filters.manualInput.includes(parameter),
      ).map((column) => ({ ...column, chartable: false })),
      ...(dataSeries?.metaData?.parameters ?? [])
        .filter(
          ({ code }) =>
            !BREEDER_REGISTRATION_PARAMETERS.includes(code) && filters.kpiFilters.includes(code),
        )
        .map((parameter) => ({
          parameter: parameter.code,
          decimals: parameter.numberOfDecimals ?? 0,
          unit: parameter.unit ?? '',
          chartable: graphSeries.some(
            (series) =>
              series.parameterCode === parameter.code &&
              series.values.some(({ value }) => Number.isFinite(value)),
          ),
        })),
    ];
  });
  protected readonly sortDirection = signal<'asc' | 'desc'>('asc');
  protected readonly rows = computed(() => {
    const direction = this.sortDirection();
    return [...(this.flockWeekDataSet().data ?? [])].sort((left, right) =>
      direction === 'asc' ? left.week - right.week : right.week - left.week,
    );
  });

  protected setSortDirection(direction: 'asc' | 'desc'): void {
    this.sortDirection.set(direction);
  }

  protected openChart(column: BreederColumn): void {
    this.store.dispatch(
      loadBreederFlockChart({
        flock: this.flock(),
        kpi: {
          name: column.parameter,
          translationKey: `flock-overview.week-registrations.breeder-kpis.${column.parameter}`,
          unit: column.unit,
          decimalsPrecision: column.decimals,
        },
      }),
    );
  }

  protected formatValue(row: FlockWeekData, column: BreederColumn): string {
    const value = row.values?.find(({ parameter }) => parameter === column.parameter)?.value;
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return '-';
    }

    return (
      this.decimalPipe.transform(value, `1.${column.decimals}-${column.decimals}`, this.locale) ??
      '-'
    );
  }
}
